import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { InterviewCard, LoadingBlock } from "@/components/site/Cards";

export default function Interviews() {
  const [sp, setSp] = useSearchParams();
  const [interviews, setInterviews] = useState([]);
  const [topics, setTopics] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = useMemo(
    () => ({
      topic: sp.get("topic") || "",
      industry: sp.get("industry") || "",
      sort: sp.get("sort") || "newest",
      q: sp.get("q") || "",
    }),
    [sp],
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [t, i] = await Promise.all([api.get("/topics"), api.get("/industries")]);
        setTopics(t.data);
        setIndustries(i.data);
      } catch (err) {
        console.error("Failed to load topic/industry filters:", err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/interviews", {
          params: {
            topic: filters.topic || undefined,
            industry: filters.industry || undefined,
            q: filters.q || undefined,
            sort: filters.sort,
          },
        });
        setInterviews(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [filters]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value);
    else next.delete(key);
    setSp(next);
  };

  return (
    <div data-testid="interviews-page">
      <section className="border-b border-oc-hairline">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="eyebrow text-oc-coral mb-6">Read</div>
          <h1 className="hero-headline text-5xl md:text-7xl text-oc-ink max-w-4xl">
            Articles, interviews, and frameworks.
          </h1>
          <p className="mt-6 font-editorial text-xl text-oc-ink-soft max-w-2xl leading-snug">
            Long-form conversations and practical writing from people actually operating businesses. Filter by topic, industry, or format, or browse by the problem you&#39;re trying to solve.
          </p>
          <div className="mt-6">
            <a
              href="/topics"
              className="inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-blue text-oc-blue pb-1 hover:text-oc-coral hover:border-oc-coral"
            >
              Browse by problem &rarr;
            </a>
          </div>
        </div>
      </section>

      <section className="border-b border-oc-hairline sticky top-16 md:top-20 z-40 bg-oc-beige/95 backdrop-blur">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
          <div className="eyebrow text-oc-ink-muted">Filter</div>
          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              label="Topic"
              value={filters.topic}
              options={[{ value: "", label: "All topics" }, ...topics.map((t) => ({ value: t.slug, label: t.name }))]}
              onChange={(v) => setFilter("topic", v)}
              testId="filter-topic"
            />
            <FilterSelect
              label="Industry"
              value={filters.industry}
              options={[{ value: "", label: "All industries" }, ...industries.map((i) => ({ value: i.slug, label: i.name }))]}
              onChange={(v) => setFilter("industry", v)}
              testId="filter-industry"
            />
            <FilterSelect
              label="Sort"
              value={filters.sort}
              options={[
                { value: "newest", label: "Newest first" },
                { value: "oldest", label: "Oldest first" },
                { value: "featured", label: "Featured" },
              ]}
              onChange={(v) => setFilter("sort", v)}
              testId="filter-sort"
            />
            {(filters.topic || filters.industry || filters.q) && (
              <button
                data-testid="filter-clear"
                onClick={() => setSp(new URLSearchParams())}
                className="font-ui text-xs font-bold uppercase tracking-widest text-oc-coral hover:text-oc-ink"
              >
                Clear
              </button>
            )}
          </div>
          <div className="md:ml-auto font-ui text-sm text-oc-ink-muted">
            {loading ? "Loading..." : `${interviews.length} result${interviews.length === 1 ? "" : "s"}`}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-20">
          {loading ? (
            <LoadingBlock />
          ) : interviews.length === 0 ? (
            <div className="py-32 text-center font-editorial text-xl text-oc-ink-muted">
              No interviews match those filters yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {interviews.map((iv) => (
                <InterviewCard key={iv.slug} interview={iv} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange, testId }) {
  return (
    <label className="inline-flex items-center gap-2">
      <span className="sr-only">{label}</span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 pl-3 pr-8 bg-white border border-oc-hairline font-ui text-sm hover:border-oc-ink focus:border-oc-ink outline-none cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
